// The per-copy list: TanStack useInfiniteQuery over POST /copies/search
// (plan 2026-10-16 W2, A1/A2).
//
//  - Keyed ['copies', collectionId, userId, <canonical filters>, sort, dir]:
//    the canonical filter JSON is the SAME object the server hashes into the
//    cursor, so a cursor is only ever passed back with the identical filters,
//    sort and direction it was issued for.
//  - `next_cursor` is passed back UNCHANGED. A 422 `bad cursor` on a later
//    page (a cursor from an older server, or one that no longer fits) drops
//    every page and restarts from page 1.
//  - The transport (bearer, 401 refresh, 429/503 backoff) lives in
//    api/collectionCopies.ts; TanStack's own retry is off so the policy is
//    applied once. Cancellation is TanStack's AbortSignal.
//  - Only enabled while the capability is per_copy.
//  - The page size travels IN the pageParam ({cursor, limit}), not the key:
//    a binder catching up to a deep `?page=` asks for bigger pages
//    (`nextLimit`) without resetting the query, and a refetch replays each
//    page with the size it was first read at, so coverage never shrinks.
import { useLayoutEffect, useMemo, useRef } from 'react'
import { replaceEqualDeep, useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'
import {
  CopiesError, canonicalFilterJson, canonicalizeFilters, fetchDeckLinks, fetchValueHistory, isCopiesError, isGroupUnsupported, searchCopies, carryLineRevs,
  searchCopyGroups,
  type BinderRef, type CopiesFacets, type CopiesFilters, type CopiesGroupPage, type CopiesPage, type CopiesSummary, type CopyGroup,
  type CopyItem, type DeckLinks, type ValueHistory,
} from '../api/collectionCopies'
import {
  CAPABILITY, CARD_LINES_ORDER, DETAIL, ERR, GROUP_KIND, GROUP_MODE, QK, type Capability, type CopiesInclude, type CopySort, type GroupMode, type SortDir,
  type ValueRange,
} from '../api/collectionCopies.codes'
import { markGroupSupported, markGroupUnsupported } from '../lib/copiesGroupCapability'

export const COPIES_PAGE_SIZE = 100
/** The server's largest page (MAX_LIST_LIMIT). */
export const COPIES_MAX_LIMIT = 500

/** One page's request: the cursor (null = page 1) and its size. */
export interface CopiesPageParam {
  cursor: string | null
  limit: number
}

function clampLimit(n: number): number {
  return Math.max(1, Math.min(COPIES_MAX_LIMIT, Math.floor(Number.isFinite(n) ? n : COPIES_PAGE_SIZE)))
}

/** A page param as stored, tolerating the bare-cursor shape (seeded caches). */
function pageParamOf(raw: unknown, limit: number): CopiesPageParam {
  if (raw && typeof raw === 'object' && 'limit' in raw) {
    const p = raw as CopiesPageParam
    return { cursor: p.cursor ?? null, limit: clampLimit(p.limit) }
  }
  return { cursor: typeof raw === 'string' ? raw : null, limit: clampLimit(limit) }
}

export function copiesQueryKey(
  collectionId: string, userId: string, filters: CopiesFilters, sort: CopySort, dir: SortDir, facets = false,
): QueryKey {
  // `facets` joins the key (plan 2026-10-18 R9'): a page with facets and one
  // without are different answers.
  return [QK.copies, collectionId, userId, canonicalFilterJson(canonicalizeFilters(filters)), sort, dir, facets]
}

export interface UseCopiesArgs {
  collectionId: string | null
  userId: string | null
  mode: Capability
  filters: CopiesFilters
  sort: CopySort
  dir: SortDir
  limit?: number
  /**
   * The size of the NEXT page, given how many lines are loaded (clamped to
   * 1..COPIES_MAX_LIMIT). Omitted = every page is `limit`.
   */
  nextLimit?: (loaded: number) => number
  /** False keeps the query idle (a screen section that does not show the list). */
  enabled?: boolean
  /**
   * While a new SEARCH (only the filters changed) is pending, keep showing
   * the previous search's rows as placeholder data instead of a skeleton.
   * Placeholder rows never page: no next page, no fetchNextPage, until the
   * new search answers.
   */
  keepPrevious?: boolean
  /**
   * A cache entry of its own under the same root (the header's highlights):
   * appended to the key, so a short read never becomes page 1 of the list
   * with the same filters and sort. Same [copies, collectionId] prefix, so
   * every copy write invalidates it like the list.
   */
  keyTail?: string
  /**
   * `include: ["spark"]` (Showcase): the lines carry spark_7d. Joins the key,
   * so a page read with sparklines and one without are different answers.
   */
  include?: readonly CopiesInclude[]
}

/** The key segment of an include list (absent when empty). */
function includeTail(include: readonly CopiesInclude[] | undefined): string[] {
  // eslint-disable-next-line i18next/no-literal-string -- query key segment
  return include?.length ? [`include:${[...include].sort().join(',')}`] : []
}

/**
 * Placeholder data for keepPrevious: the previous query's data, but only for
 * a new SEARCH inside the same SCOPE. The rest of the key (collection, user,
 * sort, dir, facets, tail) must be equal, and so must the scope fields of the
 * canonical filters: the status set (the Cards tab's status chips), the
 * binder, the card names of a pocket, and the group. Only the search-ish
 * fields (q, syntax, the panel's structured filters) may differ. Switching
 * Owned to Trade, or binder A to B, shows the skeleton: another scope's rows
 * must never stand in for this one's.
 */
function previousSearch<D>(prev: D | undefined, prevQuery: { queryKey: QueryKey } | undefined, key: QueryKey): D | undefined {
  if (!prev || !prevQuery) return undefined
  const a = prevQuery.queryKey
  if (a.length !== key.length) return undefined
  for (let i = 0; i < key.length; i++) {
    if (i === FILTER_KEY_INDEX) continue
    if (a[i] !== key[i]) return undefined
  }
  const scopeA = scopeOfKey(a)
  return scopeA !== null && scopeA === scopeOfKey(key) ? prev : undefined
}

/** The canonical filter JSON's index in both list keys. */
const FILTER_KEY_INDEX = 3

/** The canonical filter fields that are the list's SCOPE, never a search refinement. */
const SCOPE_FIELDS = ['status', 'binder', 'name_keys', 'group'] as const

/** The scope fields of a key's canonical filters as a comparable string; null when unreadable. */
export function scopeOfKey(key: QueryKey): string | null {
  try {
    const f = JSON.parse(String(key[FILTER_KEY_INDEX])) as Record<string, unknown>
    return JSON.stringify(SCOPE_FIELDS.map(k => f[k] ?? null))
  } catch {
    return null
  }
}

interface ListOptionsArgs {
  collectionId: string
  userId: string
  filters: CopiesFilters
  sort: CopySort
  dir: SortDir
  limit: number
  key: QueryKey
  include?: readonly CopiesInclude[]
}

/**
 * The next page's param. A refetch replays the pages it had, rebuilding each
 * param through this function: page n keeps the size it was first read at.
 * The cached data is still the OLD answer while the refetch runs, so its
 * params name those sizes; a fetchNextPage asks one past them.
 */
function nextCopiesPageParam(
  queryClient: QueryClient, key: QueryKey, limit: number, last: CopiesPage, all: CopiesPage[], nextLimit?: (loaded: number) => number,
): CopiesPageParam | null | undefined {
  if (!last.next_cursor) return undefined
  const old = queryClient.getQueryData<InfiniteData<CopiesPage, unknown>>(key)?.pageParams
  const kept = old?.[all.length]
  if (kept && typeof kept === 'object' && 'limit' in kept) return { cursor: last.next_cursor, limit: pageParamOf(kept, limit).limit }
  const loaded = all.reduce((n, p) => n + p.items.length, 0)
  return { cursor: last.next_cursor, limit: clampLimit(nextLimit ? nextLimit(loaded) : limit) }
}

/** How long a list page is fresh (no refetch on mount). */
const COPIES_STALE_MS = 30_000

/**
 * The list query's options, shared by useCopies and prefetchCopiesList so a
 * prefetched page IS the query the screen reads (same key, same page params,
 * same fetcher, same paging).
 */
function copiesListOptions(queryClient: QueryClient, { collectionId, userId, filters, sort, dir, limit, key, include }: ListOptionsArgs) {
  return {
    queryKey: key,
    initialPageParam: { cursor: null, limit: clampLimit(limit) } as CopiesPageParam | null,
    retry: false,
    staleTime: COPIES_STALE_MS,
    // Lines kept as equal take the new response's rev (carryLineRevs).
    structuralSharing: (old: unknown, next: unknown) => {
      const shared = replaceEqualDeep(old, next)
      carryLineRevs(shared, next)
      return shared
    },
    queryFn: async ({ pageParam: raw, signal }: { pageParam: CopiesPageParam | null; signal: AbortSignal }) => {
      const pageParam = pageParamOf(raw, limit)
      try {
        return await searchCopies(
          { userId, collectionId, signal },
          // cursor ONLY from page 2 on: an explicit null is a 422 now.
          { filters, sort, dir, limit: pageParam.limit, include, ...(pageParam.cursor ? { cursor: pageParam.cursor } : {}) },
        )
      } catch (e) {
        if (pageParam.cursor && isCopiesError(e) && e.kind === ERR.validation && e.detail === DETAIL.badCursor) {
          // Drop every page and start over from page 1 (spec 3.1).
          setTimeout(() => { void queryClient.resetQueries({ queryKey: key, exact: true }) }, 0)
        }
        throw e
      }
    },
    getNextPageParam: (last: CopiesPage, all: CopiesPage[]) => nextCopiesPageParam(queryClient, key, limit, last, all),
  }
}

/**
 * Start the Cards list's FIRST page now, as the query useCopies will read
 * (the screen's own read then joins it instead of sending a second one).
 * CollectionPage calls it while the capability probe is in the air for a
 * pair this device remembers as per-copy (lib/copiesModeHint). Never throws.
 */
export function prefetchCopiesList(
  queryClient: QueryClient,
  args: { collectionId: string; userId: string; filters: CopiesFilters; sort: CopySort; dir: SortDir; limit?: number },
): void {
  const limit = args.limit ?? COPIES_PAGE_SIZE
  const key = copiesQueryKey(args.collectionId, args.userId, args.filters, args.sort, args.dir)
  void queryClient.prefetchInfiniteQuery(copiesListOptions(queryClient, { ...args, limit, key })).catch(() => undefined)
}

export interface UseCopiesResult {
  items: CopyItem[]
  /** From the latest page: the whole-collection counts. */
  summary: CopiesSummary | null
  /** want_value_usd / want_unpriced, which only the FIRST page carries. */
  firstSummary: CopiesSummary | null
  total: number | null
  /** Summed qty of the filtered lines ("N cards match"); null from an older server. */
  matchedQty: number | null
  copiesRev: number | null
  /** Any page is being (re)fetched: the counts may be about to change. */
  isFetching: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  /** keepPrevious: the rows are the previous search's while this one is pending. */
  isPlaceholderData: boolean
  error: unknown
  fetchNextPage: () => void
  refetch: () => void
}

export function useCopies({
  collectionId, userId, mode, filters, sort, dir, limit = COPIES_PAGE_SIZE, nextLimit, enabled: wanted = true, keyTail, keepPrevious = false,
  include,
}: UseCopiesArgs): UseCopiesResult {
  const queryClient = useQueryClient()
  // The latest nextLimit, read when TanStack asks for the next page param.
  const nextLimitRef = useRef(nextLimit)
  useLayoutEffect(() => { nextLimitRef.current = nextLimit })
  const enabled = wanted && mode === CAPABILITY.perCopy && !!collectionId && !!userId
  const baseKey = [...copiesQueryKey(collectionId ?? '', userId ?? '', filters, sort, dir), ...includeTail(include)]
  const key = keyTail ? [...baseKey, keyTail] : baseKey

  const q = useInfiniteQuery<CopiesPage, unknown, InfiniteData<CopiesPage, CopiesPageParam | null>, QueryKey, CopiesPageParam | null>({
    ...copiesListOptions(queryClient, { collectionId: collectionId ?? '', userId: userId ?? '', filters, sort, dir, limit, key, include }),
    // The latest nextLimit, read only when TanStack asks for the next page param.
    getNextPageParam: (last, all) => nextCopiesPageParam(queryClient, key, limit, last, all, nextLimitRef.current),
    enabled,
    placeholderData: keepPrevious ? (prev, prevQuery) => previousSearch(prev, prevQuery, key) : undefined,
  })

  const pages = q.data?.pages
  const items = useMemo(() => (pages ? pages.flatMap(p => p.items) : []), [pages])
  const last = pages?.[pages.length - 1]
  return {
    items,
    summary: last?.summary ?? null,
    firstSummary: pages?.[0]?.summary ?? null,
    total: last?.total ?? null,
    matchedQty: last?.matched_qty ?? null,
    copiesRev: last?.copies_rev ?? null,
    isFetching: q.isFetching,
    // Placeholder pages belong to another query key: their cursor must never page this one.
    hasNextPage: !!q.hasNextPage && !q.isPlaceholderData,
    isFetchingNextPage: q.isFetchingNextPage,
    isLoading: enabled && q.isLoading,
    isPlaceholderData: q.isPlaceholderData,
    error: q.error,
    fetchNextPage: () => { if (q.hasNextPage && !q.isFetchingNextPage && !q.isPlaceholderData) void q.fetchNextPage() },
    refetch: () => { void q.refetch() },
  }
}

/** The grouped list's key: the canonical filters WITH the group (group spec 4), and a `groups` tail. */
export function copyGroupsQueryKey(
  collectionId: string, userId: string, filters: CopiesFilters, group: Exclude<GroupMode, 'none'>, sort: CopySort, dir: SortDir,
): QueryKey {
  // eslint-disable-next-line i18next/no-literal-string -- query key segment
  return [QK.copies, collectionId, userId, canonicalFilterJson(canonicalizeFilters(filters, group)), sort, dir, false, 'groups']
}

export interface UseCopyGroupsArgs extends Omit<UseCopiesArgs, 'nextLimit'> {
  group: Exclude<GroupMode, 'none'>
  /** The size of the NEXT page (groups), given how many groups are loaded. */
  nextLimit?: (loaded: number) => number
}

export interface UseCopyGroupsResult {
  groups: CopyGroup[]
  summary: CopiesSummary | null
  /** The group count of the matching lines. */
  total: number | null
  matchedQty: number | null
  isFetching: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  /** The last read answered with groups (never placeholder data). */
  isSuccess: boolean
  /** keepPrevious: the pockets are the previous search's while this one is pending. */
  isPlaceholderData: boolean
  error: unknown
  fetchNextPage: () => void
  refetch: () => void
}

/**
 * The grouped pockets: the same paging rules as useCopies (pageParam
 * {cursor, limit}, a bad cursor restarts at page 1, TanStack retry off), over
 * the grouped cursor. A refused grouped read (isGroupUnsupported) marks the
 * session unsupported, a read with groups marks it supported; the caller
 * falls back to useCopies from the store.
 */
export function useCopyGroups({
  collectionId, userId, mode, filters, sort, dir, group, limit = COPIES_PAGE_SIZE, nextLimit, enabled: wanted = true, keepPrevious = false,
}: UseCopyGroupsArgs): UseCopyGroupsResult {
  const queryClient = useQueryClient()
  const nextLimitRef = useRef(nextLimit)
  useLayoutEffect(() => { nextLimitRef.current = nextLimit })
  const enabled = wanted && mode === CAPABILITY.perCopy && !!collectionId && !!userId
  const key = copyGroupsQueryKey(collectionId ?? '', userId ?? '', filters, group, sort, dir)

  const q = useInfiniteQuery<CopiesGroupPage, unknown, InfiniteData<CopiesGroupPage, CopiesPageParam | null>, QueryKey, CopiesPageParam | null>({
    queryKey: key,
    enabled,
    initialPageParam: { cursor: null, limit: clampLimit(limit) },
    retry: false,
    staleTime: 30_000,
    placeholderData: keepPrevious ? (prev, prevQuery) => previousSearch(prev, prevQuery, key) : undefined,
    queryFn: async ({ pageParam: raw, signal }) => {
      const pageParam = pageParamOf(raw, limit)
      try {
        // `supported` is published from a layout effect once this page is
        // COMMITTED (below), never from here: the screen must not switch to
        // pockets before the grouped query holds them (Codex r1 #2).
        return await searchCopyGroups(
          { userId: userId!, collectionId: collectionId!, signal },
          { filters, sort, dir, group, limit: pageParam.limit, ...(pageParam.cursor ? { cursor: pageParam.cursor } : {}) },
        )
      } catch (e) {
        if (isGroupUnsupported(e)) markGroupUnsupported(userId!)
        else if (pageParam.cursor && isCopiesError(e) && e.kind === ERR.validation && e.detail === DETAIL.badCursor) {
          setTimeout(() => { void queryClient.resetQueries({ queryKey: key, exact: true }) }, 0)
        }
        throw e
      }
    },
    getNextPageParam: (last, all) => {
      if (!last.next_cursor) return undefined
      const old = queryClient.getQueryData<InfiniteData<CopiesGroupPage, unknown>>(key)?.pageParams
      const kept = old?.[all.length]
      if (kept && typeof kept === 'object' && 'limit' in kept) return { cursor: last.next_cursor, limit: pageParamOf(kept, limit).limit }
      const fn = nextLimitRef.current
      const loaded = all.reduce((n, p) => n + p.groups.length, 0)
      return { cursor: last.next_cursor, limit: clampLimit(fn ? fn(loaded) : limit) }
    },
  })

  const pages = q.data?.pages
  const groups = useMemo(() => (pages ? pages.flatMap(p => p.groups) : []), [pages])
  // The committed answer with groups: from now on the session groups.
  const answered = enabled && !!pages?.length && !q.isPlaceholderData
  useLayoutEffect(() => {
    if (answered && userId) markGroupSupported(userId)
  }, [answered, userId])
  const last = pages?.[pages.length - 1]
  return {
    groups,
    summary: last?.summary ?? null,
    total: last?.total_groups ?? null,
    matchedQty: last?.matched_qty ?? null,
    isFetching: q.isFetching,
    hasNextPage: !!q.hasNextPage && !q.isPlaceholderData,
    isFetchingNextPage: q.isFetchingNextPage,
    isLoading: enabled && q.isLoading,
    isSuccess: q.isSuccess && !q.isPlaceholderData,
    isPlaceholderData: q.isPlaceholderData,
    error: q.error,
    fetchNextPage: () => { if (q.hasNextPage && !q.isFetchingNextPage && !q.isPlaceholderData) void q.fetchNextPage() },
    refetch: () => { void q.refetch() },
  }
}

/**
 * The lines behind ONE pocket, for the copy detail opened from it: a
 * per-line search under the SAME filter the pocket was counted under (the
 * grouped read's committed filters, built by `buildCopiesSearchRequest` as
 * the binder search is), intersected with the pocket: the binder, the card
 * (`name_keys`), the pocket's kind as a status filter that never widens the
 * filter's own statuses (a want pocket stays want-only) and, for a printing
 * pocket, its set and finish. Then, for a printing pocket, only the lines of
 * that very printing (the same scryfall id, else the same set and collector
 * number). The old read started from no filter, so a trade-only binder
 * search opened a pocket full of owned and proxy lines (iOS f86dc1a).
 */
export interface PocketScope {
  binder: BinderRef
  group: Exclude<GroupMode, 'none'>
  kind: CopyGroup['kind']
  name_key: string
  scryfall_id: string | null
  set: string | null
  collector_number: string | null
  finish: CopyGroup['finish']
  /** The binder search's own filters, without the binder ({} = unfiltered). */
  base: CopiesFilters
}

export function pocketScopeOf(
  g: CopyGroup, group: Exclude<GroupMode, 'none'>, binder: BinderRef, filters: CopiesFilters = {},
): PocketScope {
  // The binder is the pocket's own; the rest is the search it was read under.
  const { binder: _binder, ...base } = filters
  void _binder
  return {
    binder, group, kind: g.kind, name_key: g.name_key, scryfall_id: g.scryfall_id, set: g.set,
    collector_number: g.collector_number, finish: g.finish, base,
  }
}

// eslint-disable-next-line i18next/no-literal-string -- wire status codes
const WANT_STATUSES: readonly string[] = ['want']
// eslint-disable-next-line i18next/no-literal-string -- wire status codes
const HAVE_STATUSES: readonly string[] = ['owned', 'trade', 'proxy']

/**
 * `allowed` narrowed to the filter's own `values` when it has any (never
 * widened), else `allowed`. Case-folded, as the server's canonical form is.
 */
function narrow(values: readonly string[] | null | undefined, allowed: readonly string[]): string[] {
  if (!values?.length) return [...allowed]
  const have = new Set(values.map(v => v.toLowerCase()))
  return allowed.filter(v => have.has(v.toLowerCase()))
}

/**
 * The per-line search filters of a pocket's lines; null when the pocket
 * cannot hold any line under the filter (an empty intersection must never
 * read as "no filter" and widen the read).
 */
export function pocketFilters(s: PocketScope): CopiesFilters | null {
  const base = s.base ?? {}
  const nameKey = s.name_key.toLowerCase()
  if (base.name_keys?.length && !base.name_keys.some(k => k.toLowerCase() === nameKey)) return null
  const status = narrow(base.status, s.kind === GROUP_KIND.want ? WANT_STATUSES : HAVE_STATUSES)
  if (!status.length) return null
  const f: CopiesFilters = { ...base, name_keys: [s.name_key], binder: s.binder, status }
  if (s.group === GROUP_MODE.printing) {
    const finish = narrow(base.finish, [s.finish])
    if (!finish.length) return null
    f.finish = finish
    if (s.set) {
      const set = narrow(base.set, [s.set])
      if (!set.length) return null
      f.set = set
    }
  }
  return f
}

/** A line belongs to a printing pocket: the same printing (id, else set + number) and finish. */
export function inPocket(item: CopyItem, s: PocketScope): boolean {
  if (s.group !== GROUP_MODE.printing) return true
  if (item.finish !== s.finish) return false
  if (s.scryfall_id) return item.scryfall_id === s.scryfall_id
  if (item.scryfall_id) return false
  return (item.set ?? null) === (s.set ?? null) && (item.collector_number ?? null) === (s.collector_number ?? null)
}

export function pocketCopiesQueryKey(collectionId: string | null, userId: string | null, s: PocketScope | null): QueryKey {
  // eslint-disable-next-line i18next/no-literal-string -- query key segment
  return [QK.copiesDetail, collectionId, userId, 'pocket', s ? JSON.stringify(s) : null]
}

/** Every line of one pocket (follows cursors to the end; a repeated cursor stops it). */
export function usePocketCopies(collectionId: string | null, userId: string | null, mode: Capability, scope: PocketScope | null) {
  return useQuery<CopyItem[]>({
    queryKey: pocketCopiesQueryKey(collectionId, userId, scope),
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId && !!scope,
    retry: false,
    queryFn: async ({ signal }) => {
      // Every page until next_cursor is null (a pocket may hold more than
      // 5,000 lines: no page cap, Codex r1 #3); a cursor the server hands back
      // twice would loop forever, so it stops the read with an error instead.
      const filters = pocketFilters(scope!)
      // The binder's filter and the pocket contradict each other: no line
      // can match, so nothing is read (never a read with no filter).
      if (!filters) return []
      const out: CopyItem[] = []
      const seen = new Set<string>()
      let cursor: string | null = null
      for (;;) {
        const page: CopiesPage = await searchCopies(
          { userId: userId!, collectionId: collectionId!, signal },
          { filters, ...CARD_LINES_ORDER, limit: 500, ...(cursor ? { cursor } : {}) },
        )
        out.push(...page.items.filter(it => inPocket(it, scope!)))
        if (!page.next_cursor) return out
        if (seen.has(page.next_cursor)) throw new CopiesError(ERR.decode, { detail: DETAIL.repeatedCursor })
        seen.add(page.next_cursor)
        cursor = page.next_cursor
      }
    },
  })
}

function cardKeysOf(nameKey: string | readonly string[] | null): string[] {
  return nameKey === null ? [] : typeof nameKey === 'string' ? [nameKey] : [...nameKey]
}

/**
 * The ONE cache key of a card's lines. A single key is stored as the bare
 * string whether it came in as `'sol ring'` or `['sol ring']`, so the
 * collection's detail panel and the deck editor share one cache entry (two
 * entries for the same lines could disagree about a qty).
 */
export function cardCopiesQueryKey(collectionId: string | null, userId: string | null, nameKey: string | readonly string[] | null): QueryKey {
  const keys = cardKeysOf(nameKey)
  return [QK.copiesDetail, collectionId, userId, keys.length === 0 ? null : keys.length === 1 ? keys[0] : keys]
}

/**
 * Every copy line of ONE card (the detail panel): `name_keys: [key]`, all pages.
 * `nameKey` may also be a list (a card's full name AND its front face, from
 * lib/nameKeys `nameKeys`); the caller then keeps the lines with `sameCard`.
 */
export function useCardCopies(collectionId: string | null, userId: string | null, mode: Capability, nameKey: string | readonly string[] | null) {
  const keys = cardKeysOf(nameKey)
  return useQuery<CopyItem[]>({
    queryKey: cardCopiesQueryKey(collectionId, userId, nameKey),
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId && keys.length > 0 && keys.every(k => !!k),
    retry: false,
    queryFn: async ({ signal }) => {
      const out: CopyItem[] = []
      let cursor: string | null = null
      // A card has a handful of lines; ten pages of 500 is a hard stop.
      for (let i = 0; i < 10; i++) {
        const page: CopiesPage = await searchCopies(
          { userId: userId!, collectionId: collectionId!, signal },
          { filters: { name_keys: keys }, ...CARD_LINES_ORDER, limit: 500, ...(cursor ? { cursor } : {}) },
        )
        out.push(...page.items)
        if (!page.next_cursor) break
        cursor = page.next_cursor
      }
      return out
    },
  })
}

export function useValueHistory(collectionId: string | null, userId: string | null, mode: Capability, range: ValueRange, enabled = true) {
  return useQuery<ValueHistory>({
    queryKey: [QK.valueHistory, collectionId, userId, range],
    enabled: enabled && mode === CAPABILITY.perCopy && !!collectionId && !!userId,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => fetchValueHistory({ userId: userId!, collectionId: collectionId!, signal }, range),
  })
}

export function useDeckLinks(collectionId: string | null, userId: string | null, mode: Capability) {
  return useQuery<DeckLinks>({
    queryKey: [QK.deckLinks, collectionId, userId],
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => fetchDeckLinks({ userId: userId!, collectionId: collectionId!, signal }),
  })
}

/**
 * The collection's own facets (sets, types, subtypes, rarities, finishes)
 * for the filter panel and the smart field's suggestion scope: ONE
 * `limit: 1, facets: true` search over the status tab only, so the pickers
 * keep listing everything the user owns while other filters narrow the list.
 */
export function useCopiesFacets(
  collectionId: string | null, userId: string | null, mode: Capability, status: readonly string[], enabled = true,
) {
  const filters: CopiesFilters = { status: [...status] }
  return useQuery<CopiesFacets | null>({
    queryKey: [QK.copiesFacets, collectionId, userId, canonicalFilterJson(canonicalizeFilters(filters))],
    enabled: enabled && mode === CAPABILITY.perCopy && !!collectionId && !!userId,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const page = await searchCopies(
        { userId: userId!, collectionId: collectionId!, signal },
        { filters, ...CARD_LINES_ORDER, limit: 1, facets: true },
      )
      return page.facets ?? null
    },
  })
}
